// Guide content data — all 8 step-by-step help guides
// Each guide has: slug, title, icon, iconClass, description, sections[]
// Each section has: title, steps[] (each step has a title and body text)

import {
  faRocket, faUsers, faImages, faSitemap, faCalendarAlt,
  faEnvelope, faQrcode, faMobileAlt, faUserShield, faCogs
} from '@fortawesome/free-solid-svg-icons';

const guides = [
  {
    slug: 'getting-started',
    title: 'Getting Started with Church Aware',
    icon: faRocket,
    iconClass: 'guide-icon-start',
    description: 'A quick overview of Church Aware and your first steps after registering your organization.',
    sections: [
      {
        title: 'What is Church Aware?',
        content: `Church Aware is an all-in-one church directory platform. It gives your congregation a living, photo-rich directory that members can browse and update themselves — plus tools for events, attendance, groups, and communications. Your members access the directory through a free mobile app or the web, while you manage everything from the admin dashboard.`
      },
      {
        title: 'Your Dashboard',
        content: `After logging in you land on the Dashboard. This is your home base — you'll see at-a-glance statistics for how many families, members, and groups you have, upcoming birthdays and anniversaries for the current month, and the next few upcoming events. New organizations also see a Getting Started checklist that walks you through initial setup.`
      },
      {
        title: 'Step 1: Add Your First Family',
        content: `Click "Families" in the navigation, then click the "Add Family" button. Enter the family surname, address, phone number, and optionally upload a family photo. After saving the family you can immediately add individual members to it. We recommend starting with your own family to see how everything works.`,
        link: { text: 'Go to Families', path: '/families' }
      },
      {
        title: 'Step 2: Upload a Photo and Assign Faces',
        content: `When you add or edit a family, you can upload a family photo. Church Aware automatically detects individual faces in the photo and lets you assign each face to the correct member. This gives every member their own profile photo throughout the directory — no extra photo uploads needed.`
      },
      {
        title: 'Step 3: Explore Your Settings',
        content: `Head to Organization Settings to review your church name, address, timezone, and notification preferences. You can also upload your church logo, which appears in the directory and in emails sent to your members.`,
        link: { text: 'Go to Settings', path: '/admin/settings' }
      },
      {
        title: 'Step 4: Invite Your Team',
        content: `When you add members with email addresses, they automatically receive an invitation to create their free account. You can also generate a QR code that members scan to join the directory themselves. Consider adding a few key leaders first so they can help build out the rest of the directory.`
      },
      {
        title: 'What Next?',
        content: `Once you have a few families in the directory, explore the other features: create groups for ministries and teams, set up events, configure automated birthday and welcome messages, and download the mobile app to see the directory on the go. Each of these has its own step-by-step guide — check the links below.`
      }
    ],
    actionLinks: [
      { text: 'Go to Dashboard', path: '/dashboard' },
      { text: 'Add a Family', path: '/families/add' },
      { text: 'Organization Settings', path: '/admin/settings' },
    ]
  },
  {
    slug: 'families-and-members',
    title: 'Adding Families & Members',
    icon: faUsers,
    iconClass: 'guide-icon-families',
    description: 'Learn how to build your church directory by adding families and their individual members.',
    sections: [
      {
        title: 'How the Directory is Organized',
        content: `Church Aware organizes your directory by family. Each family has a surname, address, phone number, and optional family photo. Individual members belong to a family and have their own details: name, email, phone, birthday, and anniversary date. This family-based structure keeps related people together and makes the directory easy to browse.`
      },
      {
        title: 'Adding a New Family',
        content: `Navigate to the Families page and click "Add Family." Fill in the family surname (this is the name that appears in the directory listing), street address, city, state, zip code, and phone number. You can also upload a family photo at this time — we recommend it, since Church Aware can automatically extract individual member photos from it.`,
        link: { text: 'Add a Family', path: '/families/add' }
      },
      {
        title: 'Adding Members to a Family',
        content: `After creating a family, you'll be taken to the family detail page. Click "Add Member" to add each person in the family. For each member, enter their first and last name, email address (optional), phone number (optional), birthday (optional), and anniversary date (optional). If you enter an email address, the member will automatically receive an invitation email to create their free Church Aware account.`
      },
      {
        title: 'What Happens When You Add an Email',
        content: `When a member is added with an email address, Church Aware automatically sends them an invitation email. The email introduces the directory, explains what they can do with their account (browse the photo directory, get event notifications, receive birthday reminders), and includes a link to create their free account. You don't need to do anything extra — the invitation goes out automatically.`
      },
      {
        title: 'Editing Family and Member Details',
        content: `To edit a family, go to the family detail page and click the edit button. You can update the surname, address, phone, or photo. To edit a member, click on their name in the family detail page and then click edit. All changes are saved immediately and reflected across the web and mobile apps.`
      },
      {
        title: 'Deleting Families and Members',
        content: `To delete a member, open the member detail page and click the delete button. To delete an entire family, open the family detail page and click delete — this will also remove all members in that family. Deletions are permanent, so you'll be asked to confirm. All changes are tracked in the Activity Log so you have a record of what was removed and when.`
      },
      {
        title: 'Search & Sort',
        content: `Use the search bar on the Families page to find families by surname or member name. You can also sort the directory alphabetically or by most recently added, making it easy to find exactly who you're looking for — even in a large directory.`
      },
      {
        title: 'Mark a Member as Deceased',
        content: `On a member's detail page, administrators can click the Deceased button to mark a member as deceased. You can optionally record a date of passing. Deceased members are hidden from the regular directory listing but remain visible to administrators, so your records stay complete while keeping the active directory current.`
      },
      {
        title: 'Download Directory PDF',
        content: `Click the "Download PDF" button on the Families page to generate a printable version of your entire directory. The PDF includes family names, addresses, phone numbers, and member details — perfect for distributing a paper copy at church or keeping a backup.`,
        link: { text: 'Go to Families', path: '/families' }
      },
      {
        title: 'Bulk Import',
        content: `If you have an existing directory in a spreadsheet or CSV file, you can import families in bulk. Go to Import Directory from the admin menu, paste your data or upload a CSV, review the parsed results, and import. Church Aware will automatically group members into families and send invitation emails to anyone with an email address.`,
        link: { text: 'Import Directory', path: '/import-directory' }
      }
    ],
    actionLinks: [
      { text: 'Add a Family', path: '/families/add' },
      { text: 'View Families', path: '/families' },
      { text: 'Import Directory', path: '/import-directory' },
    ]
  },
  {
    slug: 'member-photos',
    title: 'Setting Up Member Photos',
    icon: faImages,
    iconClass: 'guide-icon-photos',
    description: 'Upload family photos and let Church Aware automatically assign individual profile pictures.',
    sections: [
      {
        title: 'Why Photos Matter',
        content: `A photo directory is far more useful than a text-only list. When members can see faces alongside names, they learn names faster, recognize people at church, and feel more connected. Church Aware makes it easy to get individual photos for every member — without asking each person to upload their own headshot.`
      },
      {
        title: 'Uploading a Family Photo',
        content: `When you add or edit a family, you'll see an option to upload a family photo. This can be any group photo of the family — a professional directory photo, a casual snapshot, or anything in between. The photo appears on the family's listing in the directory and is also used for automatic face extraction.`
      },
      {
        title: 'How Face Extraction Works',
        content: `After you upload a family photo, Church Aware automatically detects the individual faces in the image. Each detected face is cropped into its own portrait-style photo. You'll then be taken to the assignment screen where you can match each extracted face to the correct family member. This whole process takes just a few seconds.`
      },
      {
        title: 'Assigning Faces to Members',
        content: `On the assignment screen, you'll see each extracted face alongside a dropdown list of family members. Simply select which member each face belongs to and save. Once assigned, that face becomes the member's profile photo throughout the entire app — in the directory listing, member detail pages, group rosters, and the mobile app. If a face doesn't belong to any member (maybe a friend or extended family in the photo), just skip it.`
      },
      {
        title: 'Updating Photos Later',
        content: `Need to update photos? Just edit the family and upload a new family photo. Church Aware will detect the new faces and let you reassign them. Previous member photos are replaced with the newly assigned ones. You can update photos as often as you like — for example, when families have a new professional photo taken.`
      },
      {
        title: 'Tips for Great Photos',
        content: `For best results, use photos where faces are clearly visible and facing the camera. Good lighting helps the face detection work accurately. Family photos taken for church directories work perfectly. Casual photos work too, as long as faces aren't obscured by sunglasses, hats, or heavy shadows. The system can usually detect faces even in candid shots.`
      }
    ],
    actionLinks: [
      { text: 'View Families', path: '/families' },
    ]
  },
  {
    slug: 'groups',
    title: 'Organizing Members into Groups',
    icon: faSitemap,
    iconClass: 'guide-icon-groups',
    description: 'Create groups for ministries, teams, committees, and small groups.',
    sections: [
      {
        title: 'What Groups Are For',
        content: `Groups let you organize members beyond their family structure. Use them for anything: ministry teams, committees, small groups, choirs, youth groups, volunteer teams, Bible studies — any grouping that makes sense for your church. Groups make it easy to see who's involved in what and help members find their teams.`
      },
      {
        title: 'Creating a Group',
        content: `Go to the Groups page and click "Add Group." Give your group a name (e.g., "Worship Team," "Deacon Board," "Youth Ministry") and save. The group is immediately available and ready for members to be added.`,
        link: { text: 'Go to Groups', path: '/groups' }
      },
      {
        title: 'Adding Members to a Group',
        content: `Open a group and click "Add Member." You can search for any member in your directory and add them to the group. When adding a member, you can also assign a role label (e.g., "Leader," "Coordinator," "Volunteer") that appears next to their name in the group roster. Members can belong to as many groups as you like.`
      },
      {
        title: 'Viewing Groups',
        content: `The Groups page shows all your groups with a count of how many members are in each one. Click any group to see its full member roster with role labels. From the roster, you can click any member's name to go directly to their profile.`
      },
      {
        title: 'Groups on the Map',
        content: `If you use the Map feature, you can filter the map by group. This shows only the families of members in a selected group, which is helpful for planning small group meetings, organizing carpools, or understanding where your volunteers live.`,
        link: { text: 'View Map', path: '/map' }
      },
      {
        title: 'Removing Members from a Group',
        content: `To remove a member from a group, hover over their photo in the group roster and click the X button that appears. This only removes them from the group — it does not delete the member from your directory. Members can be re-added to the group at any time.`
      },
      {
        title: 'Deleting a Group',
        content: `To delete an entire group, open it and click the delete button. Deleting a group removes the group and all member assignments within it, but does not delete the members themselves — they remain in your directory and in any other groups they belong to.`
      }
    ],
    actionLinks: [
      { text: 'View Groups', path: '/groups' },
      { text: 'Add a Group', path: '/groups/add' },
      { text: 'View Map', path: '/map' },
    ]
  },
  {
    slug: 'events',
    title: 'Managing Events',
    icon: faCalendarAlt,
    iconClass: 'guide-icon-events',
    description: 'Create one-time and recurring events, and track attendance.',
    sections: [
      {
        title: 'Events Overview',
        content: `The Events feature lets you create and manage church events that appear in the directory for all members to see. Events show up on the dashboard, in the mobile app, and can trigger push notifications so members stay informed about what's happening at your church.`
      },
      {
        title: 'Creating a One-Time Event',
        content: `Go to the Events page and click "Add Event." Enter the event title, description, date, start time, and end time. You can also add a location. Click save, and the event immediately appears in the directory. Members with the mobile app receive a push notification about the new event.`,
        link: { text: 'Add an Event', path: '/events/add' }
      },
      {
        title: 'Setting Up Recurring Events',
        content: `For events that repeat (like weekly services or monthly meetings), toggle the "Recurring" option when creating an event. Choose the recurrence pattern: daily, weekly, monthly, or yearly. Set the start date and optionally an end date. Church Aware generates individual event occurrences automatically, so each one shows up on the right date in the calendar.`
      },
      {
        title: 'Editing Recurring Events',
        content: `When you edit a recurring event, Church Aware gives you three options — just like Google Calendar. You can edit "This event only" (changes only the specific occurrence), "This and future events" (updates this occurrence and all future ones), or "All events in series" (updates every occurrence). This gives you flexibility to handle schedule changes without recreating the entire series.`
      },
      {
        title: 'Deleting Events',
        content: `Deleting works the same way. For a recurring event, you can delete just one occurrence, all future occurrences, or the entire series. For a one-time event, deletion simply removes it from the calendar.`
      },
      {
        title: 'Recording Attendance',
        content: `Use the Attendance page to track how many people attend your services and events. Add a new attendance record by selecting a date, choosing an attendance type, and entering the headcount. Over time, these records build a valuable picture of your church's engagement.`,
        link: { text: 'Go to Attendance', path: '/attendance' }
      },
      {
        title: 'Attendance Types',
        content: `Create custom attendance types to categorize your records — for example, "Sunday Service," "Wednesday Night," "Youth Group," or "Bible Study." Each type tracks its own history separately, so you can compare attendance across different programs and see which ones are growing.`
      },
      {
        title: 'Viewing Attendance Trends',
        content: `Switch to the chart view on the Attendance page to visualize your attendance over time. Filter by date range (this month, last month, year-to-date, or all time) and by attendance type. The trend charts make it easy to spot patterns, seasonal changes, and long-term growth or decline.`
      }
    ],
    actionLinks: [
      { text: 'View Events', path: '/events' },
      { text: 'Add an Event', path: '/events/add' },
      { text: 'Attendance', path: '/attendance' },
    ]
  },
  {
    slug: 'communications',
    title: 'Announcements & Automations',
    icon: faEnvelope,
    iconClass: 'guide-icon-comms',
    description: 'Send announcements and set up automated messages for birthdays, welcomes, and more.',
    sections: [
      {
        title: 'Communications Overview',
        content: `Church Aware's communications system lets you reach your members through two channels: email and push notifications. You can send one-time announcements to everyone, or set up automations that send messages automatically when certain events happen (like a member's birthday or their first login).`,
        link: { text: 'Go to Communications', path: '/communications' }
      },
      {
        title: 'Sending an Announcement',
        content: `From the Communications page, click "New Announcement." Write your subject line and message body. You can preview the message before sending. When you send, Church Aware delivers the announcement via email to every member who has an email address, and via push notification to everyone with the mobile app installed.`
      },
      {
        title: 'Built-In Automations',
        content: `Church Aware comes with several pre-built automations that you can enable with a single tap. These include: Birthday Greetings (sends a happy birthday email on each member's birthday), Wedding Anniversary messages, Welcome emails (sent when a member logs in for the first time), and Directory Invite emails (sent when a new member is added with an email address).`
      },
      {
        title: 'Enabling and Disabling Automations',
        content: `Each automation has a toggle switch on the Communications page. Tap the switch to enable or disable it. When enabled, the automation runs automatically — you don't need to do anything. The label next to the switch tells you its current state. Automations can be turned on and off at any time.`
      },
      {
        title: 'Customizing Automation Messages',
        content: `Every automation has a default message template, but you can customize it. Click "Edit" on any automation to change the subject line and message body. You can use template variables like {{member.name}} for the recipient's first name, {{member.surname}} for their last name, and {{org.name}} for your church name. These get automatically replaced with the real values when the message is sent.`
      },
      {
        title: 'On-Demand Automations',
        content: `Some automations are on-demand — you trigger them manually by clicking "Send." These include the Directory Update Request (asks members to review and update their contact information) and the Re-engagement email (reaches out to members who haven't logged in recently). These are great for periodic directory maintenance.`
      },
      {
        title: 'Communication History',
        content: `The History tab on the Communications page shows all previously sent messages — both announcements and automated emails. You can see what was sent, when it was delivered, and the delivery status. This gives you a complete audit trail of every communication your organization has sent to members.`
      },
      {
        title: 'Delivery and Quiet Hours',
        content: `Church Aware respects quiet hours and daily message caps to avoid overwhelming your members. By default, messages won't be sent between 9 PM and 8 AM, and each member receives a maximum of 3 messages per day. You can adjust these settings in Organization Settings.`
      }
    ],
    actionLinks: [
      { text: 'Communications', path: '/communications' },
      { text: 'New Announcement', path: '/communications/new' },
      { text: 'Organization Settings', path: '/admin/settings' },
    ]
  },
  {
    slug: 'inviting-members',
    title: 'Inviting Members to Join',
    icon: faQrcode,
    iconClass: 'guide-icon-invite',
    description: 'Three ways to grow your directory: manual entry, bulk import, and QR code self-registration.',
    sections: [
      {
        title: 'Three Ways to Build Your Directory',
        content: `Church Aware gives you three ways to add people to your directory. You can add families and members one at a time through the admin interface, import an existing directory in bulk from a CSV or spreadsheet, or generate a QR code that people scan to add themselves. Use whichever combination works best for your church.`
      },
      {
        title: 'Option 1: Add Manually',
        content: `This is the most straightforward approach. Go to Families, click "Add Family," fill in the details, and add members. When you include an email address, the member automatically receives an invitation to create their account. This works well for adding a few families at a time or for new members joining your church.`,
        link: { text: 'Add a Family', path: '/families/add' }
      },
      {
        title: 'Option 2: Bulk Import',
        content: `If you have an existing directory in a spreadsheet, CSV, or even just a text list, you can import it all at once. Go to Import Directory, paste your data or upload a file, and Church Aware will parse it into families and members. Review the results, make any corrections, and import. Everyone with an email address will receive an invitation automatically.`,
        link: { text: 'Import Directory', path: '/import-directory' }
      },
      {
        title: 'Option 3: QR Code Self-Registration',
        content: `Generate a QR code from the admin dashboard or the mobile app. Print it and display it at your church — in the lobby, in the bulletin, or on a welcome table. When visitors or members scan the QR code with their phone, they see a form where they can enter their own family and member information. Their submission goes to your review queue for approval. This is perfect for visitor registration and new member sign-ups.`
      },
      {
        title: 'Reviewing Join Requests',
        content: `When someone submits a QR code join request, it appears on the Join Requests page. You'll also see a badge on your dashboard showing how many pending requests you have. Review each request, and approve or decline it. Approved requests are automatically added to your directory as new families with members.`,
        link: { text: 'View Join Requests', path: '/admin/join-requests' }
      },
      {
        title: 'The Invitation Email',
        content: `Every member added to the directory with an email address receives an invitation email. The email explains what Church Aware is, what the member can do with their account, and provides a link to create their free account. You can customize the invitation message template in the Communications automations section. The invitation is sent once — when the member is first added or when their email address is first entered.`
      }
    ],
    actionLinks: [
      { text: 'Add a Family', path: '/families/add' },
      { text: 'Import Directory', path: '/import-directory' },
      { text: 'Join Requests', path: '/admin/join-requests' },
    ]
  },
  {
    slug: 'users-and-admins',
    title: 'Managing Users & Admins',
    icon: faUserShield,
    iconClass: 'guide-icon-users',
    description: 'Understand admin accounts, upgrade users, and manage who has access to your directory.',
    sections: [
      {
        title: 'Admin vs. Regular Accounts',
        content: `Church Aware has two account levels. Regular members can browse the directory, view events, see groups, and update their own family's information. Administrators have full access — they can add, edit, and delete families, members, events, groups, and communications, plus access organization settings, the activity log, and user management.`
      },
      {
        title: 'Upgrading a User to Admin',
        content: `To make someone an administrator, go to the Users page and click "Make Admin" next to their username. The change takes effect immediately — the user will see the full admin interface the next time they load the app. You can create as many admin accounts as you need.`,
        link: { text: 'Go to Users', path: '/admin/users' }
      },
      {
        title: 'Managing User Accounts',
        content: `The Users page shows all user accounts in your organization. You can see each user's email, role (admin or member), login history, and notification preferences. If a user no longer needs access, you can delete their account from this page. Deleting a user account removes their login but does not remove their member record from the directory.`,
        link: { text: 'Go to Users', path: '/admin/users' }
      },
      {
        title: 'How Member Accounts Are Created',
        content: `Members create their own accounts by signing up with the same email address that's listed in your directory. When a member is first added with an email address, they automatically receive an invitation email with a link to create their free account. Only people whose email matches a member record in your directory can sign up — this keeps your directory private.`
      }
    ],
    actionLinks: [
      { text: 'Manage Users', path: '/admin/users' },
    ]
  },
  {
    slug: 'settings-and-tools',
    title: 'Settings & Tools',
    icon: faCogs,
    iconClass: 'guide-icon-settings',
    description: 'Configure your organization, review the activity log, explore the map, and manage your profile.',
    sections: [
      {
        title: 'Organization Settings',
        content: `The Settings page lets you update your church's name, street address, timezone, tagline, and logo. Your logo appears in the directory and in emails sent to members. You can also configure communication settings here, including the daily email cap per member and quiet hours for notifications.`,
        link: { text: 'Go to Settings', path: '/admin/settings' }
      },
      {
        title: 'Activity Log',
        content: `The Activity Log tracks every change made to families and members across your organization. You can see who made each change, what was modified, and when it happened. Filter by action type (create, edit, delete) or search by entity name to find specific changes. Activity history is also shown on individual Family and Member detail pages for quick reference.`,
        link: { text: 'View Activity Log', path: '/admin/activity-log' }
      },
      {
        title: 'Map',
        content: `The Map page shows your church families plotted on an interactive Google Map. Click any marker to see the family name and details. Use the group filter dropdown to show only families belonging to a specific group — helpful for planning small group meetings, organizing carpools, or understanding where your volunteers live.`,
        link: { text: 'View Map', path: '/map' }
      },
      {
        title: 'Your Profile',
        content: `Visit your Profile page to view your account details, change your password, and check your email verification status. This is your personal account page — changes here affect only your own login, not the directory.`,
        link: { text: 'Go to Profile', path: '/profile' }
      },
      {
        title: 'Notification Preferences',
        content: `On your Profile page, you can choose which notifications you receive. Options include new member alerts, birthday and anniversary reminders, event notifications, and announcement messages. Customize these to stay informed without being overwhelmed.`
      }
    ],
    actionLinks: [
      { text: 'Organization Settings', path: '/admin/settings' },
      { text: 'Activity Log', path: '/admin/activity-log' },
      { text: 'Map', path: '/map' },
      { text: 'Profile', path: '/profile' },
    ]
  },
  {
    slug: 'mobile-app',
    title: 'Using the Mobile App',
    icon: faMobileAlt,
    iconClass: 'guide-icon-mobile',
    description: 'Everything your members and admins can do on the Church Aware mobile app.',
    sections: [
      {
        title: 'Getting the App',
        content: `The Church Aware mobile app is available for both iPhone and Android. Search "Church Aware" in the App Store or Google Play to find it. The app is completely free for all members. After downloading, members log in with the same email and password they use on the web.`
      },
      {
        title: 'What Members Can Do',
        content: `Members can browse the full photo directory with tap-to-call and tap-to-text, view upcoming events and receive push notifications when new events are added, see birthdays and anniversaries for the current month, view groups and group rosters, and update their own profile and notification preferences. The mobile app gives members everything they need to stay connected on the go.`
      },
      {
        title: 'What Admins Can Do',
        content: `Admins have access to all member features plus the Admin Hub. From the Admin Hub on mobile, you can manage families and members (add, edit, delete), create and manage events (including recurring events), send announcements, configure automations, view the activity log, manage user accounts, generate QR codes for member registration, and adjust organization settings. You can fully manage your church directory from your phone.`
      },
      {
        title: 'Push Notifications',
        content: `The mobile app supports push notifications for birthdays ("It's John's birthday today!"), anniversaries, new events, and announcements from your admin team. Members can customize which notifications they want to receive in their profile settings. Push notifications are a powerful way to keep your congregation engaged and informed.`
      },
      {
        title: 'Encouraging Members to Download',
        content: `The best way to get the most out of Church Aware is to get your members on the mobile app. Here are some tips: mention the app from the pulpit, include a QR code to the app store in your bulletin, add a note about it in your welcome packet for new visitors, and make sure your invitation emails (which are sent automatically) mention the app. The more members on the app, the more connected your church becomes.`
      }
    ]
  }
];

export default guides;
